import { MARKET_SURVEY, PRODUCT_UPDATE } from 'constants/report';

import React, { useEffect } from 'react';

import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import FieldCheckBox from 'components/FieldCheckBox';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Field } from 'redux-form';
import selectTokenRoles from 'selectors/sessionTokenRoles';
import { hasSuperAdminRole } from 'utils/roleUtils';

import { SuperAdminOptions } from './SuperAdminOptions';
import {
  PageHeader,
  SLCaption,
  SLFormLabel,
} from '../../../styles/themeComponents';
import styles from '../RequestReport.module.css';

export interface AdvancedConfigProps {
  include_texture: boolean;
  texture_data_only: boolean;
  change: (field: string, value: any) => void;
  displayMode: boolean;
  report_type: SelectItem;
  isProd: boolean;
  report_id_to_inherit_parent_data_sources: string;
}

const AdvancedConfig: React.FC<AdvancedConfigProps> = ({
  include_texture,
  texture_data_only,
  change,
  displayMode,
  report_type,
  isProd,
  report_id_to_inherit_parent_data_sources,
}) => {
  const { t } = useTranslation();
  const userRoles = useSelector((state) => selectTokenRoles(state));
  const isSuperAdmin = hasSuperAdminRole(userRoles);

  useEffect(() => {
    if (texture_data_only) {
      change('texture_only', true);
    } else {
      change('texture_only', false);
    }
  }, [texture_data_only]);

  const advanced_config_checkbox_options = [
    {
      param: 'include_texture',
      label: 'Include Texture Analysis',
      value: true,
      group: 'texture',
    },
    {
      param: 'texture_data_only',
      label: 'Only Texture Data Available',
      disabled: !include_texture,
      group: 'texture',
      caption: 'Check this when the products only have texture reviews and no flavor data. This will automatically run a texture only report.',
    },
    {
      param: 'texture_only',
      label: 'Texture Only Report',
      disabled: !include_texture || texture_data_only,
      value: texture_data_only ? true : undefined,
      group: 'texture',
      caption: 'Only report on texture, skip flavor analysis. Use when you have full data but only want texture results.',
    },
    {
      param: 'use_hyperlocal_glp',
      label: 'Use Hyperlocal GLP',
      value: false
    },
    {
      param: 'showConfidence',
      label: 'Show Texture Confidence Thresholds',
      disabled: !include_texture,
      group: 'texture',
    },
    {
      param: 'show_TC_se',
      label: 'Show Texture Cluster Uncertainty',
      disabled: !include_texture,
      group: 'texture',
    },
    {
      param: 'combine_artificial_sweeteners',
      label: 'Combine Artificial Sweeteners',
    },
    {
      param: 'wipe_out_imputed_ggvars',
      label: 'Wipe Out Imputed GGVars',
    },
  ];

  return (
    <div>
      {!displayMode && (
        <div>
          <PageHeader>{t('navigation.reports')}</PageHeader>
          <h3 className={styles.productFieldsTitle}>
            {`${t('reports.createReportRequest')}: ${
              report_type.value === MARKET_SURVEY
                ? t('reports.marketSurvey')
                : t('reports.productReport')
            }`}
          </h3>
        </div>
      )}

      <h4 className={styles.productFieldsTitle}>
        {t('reports.advancedReportConfig')}
      </h4>

      <div className={styles.sectionContainer}>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            Textures
          </AccordionSummary>
          <AccordionDetails>
            <div className={styles.sectionContainer}>
              {advanced_config_checkbox_options
                .filter((d) => d.group == 'texture')
                .map((d, i) => (
                  <div
                    className={styles.sectionContainer}
                    key={i + '-texture-' + d.value}
                  >
                    <Field
                      name={d.param}
                      component={FieldCheckBox}
                      label={d.label}
                      disabled={d.disabled || displayMode}
                      value={d.value ?? false}
                    />
                    {d.caption && <SLCaption>{d.caption}</SLCaption>}
                  </div>
                ))}
            </div>
          </AccordionDetails>
        </Accordion>
        <Accordion defaultExpanded>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            Other Advanced Configuration Options
          </AccordionSummary>
          <AccordionDetails>
            <div className={styles.sectionContainer}>
              {report_type.value === MARKET_SURVEY && (
                <>
                  <Field
                    name="bootstrap_pq"
                    component={FieldCheckBox}
                    label="Bootstrap PQ Simulation"
                    disabled={displayMode}
                  />
                  <SLCaption>
                    Runs 100 bootstrap iterations that resample each product's
                    panel and retrain the PQ model to measure how stable PQ
                    scores are. Outputs 90% confidence intervals and estimates
                    how many panelists are needed for ±0.1 precision. Runs
                    async from the report. Results will appear in the
                    project directory when finished.
                  </SLCaption>
                </>
              )}
              {advanced_config_checkbox_options
                .filter((d) => d.group != 'texture')
                .map((d, i) => (
                  <div
                    className={styles.sectionContainer}
                    key={i + '-not-texture-' + d.value}
                  >
                    <Field
                      name={d.param}
                      component={FieldCheckBox}
                      label={d.label}
                      disabled={d.disabled || displayMode}
                      value={d.value ?? false}
                    />
                    {d.caption && <SLCaption>{d.caption}</SLCaption>}
                  </div>
                ))}
            </div>
          </AccordionDetails>
        </Accordion>
        {report_type.value === PRODUCT_UPDATE && (
          <Accordion defaultExpanded>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              Report Update
            </AccordionSummary>
            <AccordionDetails>
              <div className={styles.sectionContainer}>
                <SLFormLabel>Update Type</SLFormLabel>
                <Field
                  name="update_products.version_controled_translation"
                  component={FieldCheckBox}
                  label="Version Controled Translation"
                  disabled={displayMode}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      change('update_products.ensure_exact_pq', false);
                    }
                  }}
                />
                <Field
                  name="update_products.ensure_exact_pq"
                  component={FieldCheckBox}
                  label="Ensure Exact PQ"
                  disabled={displayMode}
                  onChange={(e, newValue) => {
                    if (newValue) {
                      change('update_products.version_controled_translation', false);
                    }
                  }}
                />
              </div>
            </AccordionDetails>
          </Accordion>
        )}
        {isSuperAdmin && (
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              Super Admin Options
            </AccordionSummary>
            <AccordionDetails>
              <SuperAdminOptions
                change={change}
                displayMode={displayMode}
                report_id_to_inherit_parent_data_sources={
                  report_id_to_inherit_parent_data_sources
                }
              />
            </AccordionDetails>
          </Accordion>
        )}
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
          >
            Monde Nissin Only
          </AccordionSummary>
          <AccordionDetails>
            <div className={styles.sectionContainer}>
              <Field
                name="only_include_parents"
                component={FieldCheckBox}
                label="Select only parents in the target demographic"
                disabled={displayMode}
              />
              <Field
                name="include_archetypes"
                component={FieldCheckBox}
                label="Include Archetypes"
                onChange={(newValue) =>
                  change('include_archetypes', newValue?.value)
                }
                disabled={displayMode}
              />
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};

export default AdvancedConfig;
